import { useState, useCallback } from 'react';
import type { ComputerLesson } from '../../lib/types';
import { saveQuizResult, saveProgress } from '../../lib/db';
import AiTutorButton from '../../components/AiTutorButton';

interface Props {
  lesson: ComputerLesson;
  onBack: () => void;
  onComplete: () => void;
}

type Phase = 'reading' | 'quiz' | 'result';

export default function ComputerLessonView({ lesson, onBack, onComplete }: Props) {
  const [phase, setPhase] = useState<Phase>('reading');
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);

  const total = lesson.quiz.length;
  const question = lesson.quiz[currentQ];

  const handleSelect = useCallback(
    (idx: number) => {
      if (answered) return;
      setSelected(idx);
      setAnswered(true);
      if (idx === question.correctIndex) {
        setCorrectCount((c) => c + 1);
      }
    },
    [answered, question],
  );

  const handleNext = useCallback(async () => {
    if (currentQ + 1 < total) {
      setCurrentQ((q) => q + 1);
      setSelected(null);
      setAnswered(false);
    } else {
      // Quiz finished — persist results
      // correctCount already includes the current question (updated in handleSelect)
      try {
        await saveQuizResult({
          lessonId: lesson.id,
          moduleId: 'computer',
          timestamp: new Date().toISOString(),
          score: correctCount,
          total,
        });

        await saveProgress({
          lessonId: lesson.id,
          moduleId: 'computer',
          completedAt: new Date().toISOString(),
          score: Math.round((correctCount / total) * 100),
          details: { quizScore: correctCount, quizTotal: total },
        });
      } catch {
        // Offline — progress will be saved next time
      }

      setPhase('result');
    }
  }, [currentQ, total, correctCount, selected, question, lesson.id]);

  /* ─── Reading phase ─── */
  if (phase === 'reading') {
    return (
      <div className="cl-view">
        <button className="cl-back-btn" onClick={onBack} aria-label="بازگشت">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          بازگشت
        </button>

        <h2 className="cl-title">{lesson.title}</h2>

        {lesson.illustration && (
          <div className="cl-illustration">
            <img
              src={`/illustrations/${lesson.illustration}`}
              alt={lesson.title}
              loading="lazy"
            />
          </div>
        )}

        <div className="cl-explanation">{lesson.explanation}</div>

        <div className="cl-steps-section">
          <h3 className="cl-steps-heading">مراحل انجام</h3>
          <ol className="cl-steps-list">
            {lesson.steps.map((step, i) => (
              <li key={i} className="cl-step-item">{step}</li>
            ))}
          </ol>
        </div>

        <div className="cl-ai-helper">
          <AiTutorButton
            module="computer"
            buttonText="راهنمای هوشمند این درس"
            context={`Computer lesson: ${lesson.title}. Explanation: ${lesson.explanation}. Steps: ${lesson.steps.join(' | ')}`}
            feedbackPlaceholder="اگر این درس برایتان سخت بود، اینجا بنویسید تا بازخورد دقیق‌تری بگیرید..."
          />
        </div>

        <button
          className="btn-primary cl-start-quiz-btn"
          onClick={() => setPhase('quiz')}
        >
          شروع آزمون
        </button>
      </div>
    );
  }

  /* ─── Quiz phase ─── */
  if (phase === 'quiz') {
    return (
      <div className="cl-view">
        <div className="cl-quiz-header">
          <span className="cl-quiz-counter">
            سوال {currentQ + 1} از {total}
          </span>
          <div className="progress-bar cl-quiz-progress">
            <div
              className="progress-fill"
              style={{ width: `${((currentQ + 1) / total) * 100}%` }}
            />
          </div>
        </div>

        <p className="cl-question">{question.question}</p>

        <div className="cl-options">
          {question.options.map((opt, idx) => {
            let cls = 'quiz-option';
            if (answered) {
              if (idx === question.correctIndex) cls += ' correct';
              else if (idx === selected) cls += ' wrong';
            } else if (idx === selected) {
              cls += ' selected';
            }
            return (
              <button
                key={idx}
                className={cls}
                onClick={() => handleSelect(idx)}
                disabled={answered}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {answered && (
          <div className="cl-feedback-area">
            <p className={`cl-feedback ${selected === question.correctIndex ? 'cl-feedback-correct' : 'cl-feedback-wrong'}`}>
              {selected === question.correctIndex
                ? 'آفرین! جواب درست است.'
                : 'پاسخ نادرست بود. جواب صحیح مشخص شده است.'}
            </p>
            <button className="btn-primary cl-next-btn" onClick={handleNext}>
              {currentQ + 1 < total ? 'سوال بعدی' : 'مشاهده نتیجه'}
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ─── Result phase ─── */
  const scorePercent = Math.round((correctCount / total) * 100);

  return (
    <div className="cl-view cl-result">
      <div className="cl-result-card">
        <div className="cl-result-icon">
          {scorePercent >= 70 ? (
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12l2.5 2.5L16 9" />
            </svg>
          ) : (
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4" />
              <circle cx="12" cy="16" r="0.5" fill="var(--color-warning)" />
            </svg>
          )}
        </div>

        <h2 className="cl-result-title">نتیجه آزمون</h2>

        <p className="cl-result-score">
          {correctCount} از {total} پاسخ صحیح
        </p>

        <div className="cl-result-percent">{scorePercent}٪</div>

        <p className="cl-result-message">
          {scorePercent === 100
            ? 'عالی! همه جواب\u200Cها درست بود!'
            : scorePercent >= 70
              ? 'خیلی خوب بود! ادامه بده!'
              : 'نگران نباش، دوباره تلاش کن. تمرین باعث پیشرفت می\u200Cشود!'}
        </p>
      </div>

      <div className="cl-result-actions">
        <button className="btn-primary cl-result-btn" onClick={onComplete}>
          درس بعدی
        </button>
        <button className="btn-secondary cl-result-btn" onClick={onBack}>
          بازگشت به لیست درس\u200Cها
        </button>
      </div>
    </div>
  );
}
